import { useState, useEffect } from 'react';
import { combineShows } from '../../../util/util';
import { formatDateRange } from '../../../util/data-factory';
import DropdownPicker from '../ui/DropdownPicker';
import Modal from './Modal';
import ModalActions from './ModalActions';
import { useToast } from '../../ui/Toast';

function ShowCombineModal({
  isModalOpen,
  setIsModalOpen,
  selectedShow,
  combineTargets,
  onShowsCombined,
}) {
  const { showSuccess, showError } = useToast();
  const [combineTarget, setCombineTarget] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setCombineTarget(null);
    setIsLoading(false);
  }, [isModalOpen]);

  const handleCombine = async () => {
    if (!combineTarget || !selectedShow) return;
    setIsLoading(true);
    const targetShow = combineTargets.find(s => String(s.show_id) === combineTarget);
    try {
      await combineShows(targetShow.show_id, selectedShow.show_id);
      onShowsCombined?.(targetShow.show_id, selectedShow.show_id);
      setIsModalOpen(false);
      showSuccess('Shows combined.');
    } catch (error) {
      console.error('Error combining shows:', error);
      showError(`Failed to combine shows: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const hasTargets = combineTargets?.length > 0;

  return (
    <Modal
      isOpen={isModalOpen}
      onClose={() => !isLoading && setIsModalOpen(false)}
      title={`Combine "${selectedShow?.show_title}" into another show?`}
      maxWidth="max-w-md"
      clipContent={false}
    >
      {hasTargets ? (
        <>
          <p className="text-sm text-slate-400 mb-4">
            Use this when two entries are really the same show — pick the one to merge
            into below. "{selectedShow?.show_title}" will be hidden from the feed and
            search, and its views, clicks, and seen counts will be attributed to
            the show you pick, so nothing gets lost or double-counted.
          </p>
          <div>
            <DropdownPicker
              options={combineTargets.map(s => ({
                key: String(s.show_id),
                label: s.display_title ?? s.show_title,
                sublabel: formatDateRange(s.start_date, s.end_date),
              }))}
              value={combineTarget}
              onChange={setCombineTarget}
              placeholder="Select a show…"
              width="w-full"
              variant="form"
            />
          </div>
        </>
      ) : (
        <p className="text-sm text-slate-400">
          There are no other shows at this venue to combine with.
        </p>
      )}

      <ModalActions
        onCancel={() => setIsModalOpen(false)}
        onConfirm={handleCombine}
        confirmLabel="Combine"
        confirmClassName="btn-primary w-28"
        showConfirm={hasTargets}
        isLoading={isLoading}
        confirmDisabled={!combineTarget}
      />
    </Modal>
  );
}

export default ShowCombineModal;
